import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  AgentStreamEvents,
  ResponseArtifactCreatedEvent,
} from '@dreamai/types';
import { usePusher } from '../contexts/PusherContext';

type ArtifactCreatedCallback = (data: ResponseArtifactCreatedEvent) => void;
type ArtifactUpdatedCallback = (data: any) => void;

interface ResponseArtifactEventContextType {
  registerCreatedCallback: (
    callbackId: string,
    callback: ArtifactCreatedCallback,
  ) => void;
  unregisterCreatedCallback: (callbackId: string) => void;
  registerUpdatedCallback: (
    callbackId: string,
    callback: ArtifactUpdatedCallback,
  ) => void;
  unregisterUpdatedCallback: (callbackId: string) => void;
}

const ResponseArtifactEventContext =
  createContext<ResponseArtifactEventContextType | null>(null);

/** Null outside the provider (public share pages) instead of throwing. */
export const useOptionalResponseArtifactEvents =
  (): ResponseArtifactEventContextType | null =>
    useContext(ResponseArtifactEventContext);

export const useResponseArtifactEvents =
  (): ResponseArtifactEventContextType => {
    const context = useContext(ResponseArtifactEventContext);
    if (!context) {
      throw new Error(
        'useResponseArtifactEvents must be used within a ResponseArtifactEventProvider',
      );
    }
    return context;
  };

interface ResponseArtifactEventProviderProps {
  children: React.ReactNode;
}

export const ResponseArtifactEventProvider: React.FC<
  ResponseArtifactEventProviderProps
> = ({ children }) => {
  const pusherContext = usePusher(true);

  const createdCallbacksRef = useRef<Record<string, ArtifactCreatedCallback>>(
    {},
  );
  const updatedCallbacksRef = useRef<Record<string, ArtifactUpdatedCallback>>(
    {},
  );

  // Single Pusher binding that dispatches to all registered callbacks
  useEffect(() => {
    if (!pusherContext?.pusherChannel) return;

    const handleCreated = (data: ResponseArtifactCreatedEvent) => {
      for (const cb of Object.values(createdCallbacksRef.current)) {
        try {
          cb(data);
        } catch (err) {
          console.error('Error in response artifact created callback:', err);
        }
      }
    };

    const handleUpdated = (data: any) => {
      for (const cb of Object.values(updatedCallbacksRef.current)) {
        try {
          cb(data);
        } catch (err) {
          console.error('Error in response artifact updated callback:', err);
        }
      }
    };

    pusherContext.pusherChannel.bind(
      AgentStreamEvents.RESPONSE_ARTIFACT_CREATED,
      handleCreated,
    );
    pusherContext.pusherChannel.bind(
      AgentStreamEvents.RESPONSE_ARTIFACT_UPDATED,
      handleUpdated,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentStreamEvents.RESPONSE_ARTIFACT_CREATED,
        handleCreated,
      );
      pusherContext.pusherChannel?.unbind(
        AgentStreamEvents.RESPONSE_ARTIFACT_UPDATED,
        handleUpdated,
      );
    };
  }, [pusherContext?.pusherChannel]);

  const registerCreatedCallback = useCallback(
    (callbackId: string, callback: ArtifactCreatedCallback) => {
      createdCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterCreatedCallback = useCallback((callbackId: string) => {
    delete createdCallbacksRef.current[callbackId];
  }, []);

  const registerUpdatedCallback = useCallback(
    (callbackId: string, callback: ArtifactUpdatedCallback) => {
      updatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterUpdatedCallback = useCallback((callbackId: string) => {
    delete updatedCallbacksRef.current[callbackId];
  }, []);

  return (
    <ResponseArtifactEventContext.Provider
      value={{
        registerCreatedCallback,
        unregisterCreatedCallback,
        registerUpdatedCallback,
        unregisterUpdatedCallback,
      }}
    >
      {children}
    </ResponseArtifactEventContext.Provider>
  );
};
